import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';

type UserRole = 'owner' | 'admin' | 'recruiter' | 'viewer';

interface Invitation {
  invitationId: string;
  inviteeEmail: string;
  role: UserRole;
  inviterUserId: string;
  inviterName?: string;
  expiresAt: string | number;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
}

interface PendingInvitationsTableProps {
  invitations: Invitation[];
  users: Array<{ userId: string; name: string }>;
  onInvitationCancelled: () => void;
}

const roleLabels: Record<UserRole, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  recruiter: 'Recrutador',
  viewer: 'Visualizador',
};

export function PendingInvitationsTable({
  invitations,
  users,
  onInvitationCancelled,
}: PendingInvitationsTableProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCancelInvitation = async (
    invitationId: string,
    email: string
  ) => {
    if (
      !confirm(
        `Tem certeza que deseja cancelar o convite para ${email}?`
      )
    ) {
      return;
    }

    setError(null);
    setLoadingId(invitationId);

    try {
      await apiClient.delete(`/invitations/${invitationId}`);
      onInvitationCancelled();
    } catch (err: any) {
      setError(err.message || 'Falha ao cancelar convite');
    } finally {
      setLoadingId(null);
    }
  };

  const getInviterName = (inviterUserId: string) => {
    const inviter = users.find((u) => u.userId === inviterUserId);
    return inviter?.name || 'Desconhecido';
  };

  const formatExpiresAt = (expiresAt: string | number) => {
      // expiresAt comes as Unix timestamp (seconds) from DynamoDB
      // It may arrive as number or string depending on serialization
      const raw = typeof expiresAt === 'number' ? expiresAt : Number(expiresAt);
      // Unix timestamps in seconds are < 10 billion; milliseconds are > 1 trillion
      const ms = raw > 9999999999 ? raw : raw * 1000;
      const date = new Date(ms);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return 'Expirado';
      } else if (diffDays === 0) {
        return 'Expira hoje';
      } else if (diffDays === 1) {
        return 'Expira amanhã';
      } else {
        return `Expira em ${diffDays} dias`;
      }
    };

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhum convite pendente
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
          {error}
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>E-mail</TableHead>
            <TableHead>Função</TableHead>
            <TableHead>Convidado por</TableHead>
            <TableHead>Expira</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.map((invitation) => {
            const isLoading = loadingId === invitation.invitationId;

            return (
              <TableRow key={invitation.invitationId}>
                <TableCell className="font-medium">
                  {invitation.inviteeEmail}
                </TableCell>
                <TableCell>{roleLabels[invitation.role]}</TableCell>
                <TableCell>{getInviterName(invitation.inviterUserId)}</TableCell>
                <TableCell>
                  <span
                    className={
                      formatExpiresAt(invitation.expiresAt).includes('Expirado')
                        ? 'text-red-600'
                        : ''
                    }
                  >
                    {formatExpiresAt(invitation.expiresAt)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handleCancelInvitation(
                        invitation.invitationId,
                        invitation.inviteeEmail
                      )
                    }
                    disabled={isLoading}
                  >
                    {isLoading ? 'Cancelando...' : 'Cancelar'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
